import { Container, ContainerSize, Heading, Text, Wrapper, List, ListItem, HeadingSize, HeadingColor } from '@rebootui';
import CodeSnippet from "../../../components/controls/CodeSnippet";
import CodePreview from "../../../components/controls/CodePreview";
import CodeHighlighter from "../../../components/controls/CodeHighlighter";

type TypographyPageProps = {};

const TypographyPage = ({ }: TypographyPageProps) => {
    return (
        <Container size={ContainerSize.Fluid}>

            <Heading el="h1" className="fw-semibold mb-3 ls-1">
                Typography
            </Heading>
            <Text el="p" className="mb-4">
                Documentation and examples for Bootstrap typography, including global settings, headings, body text, lists, and more.
            </Text>

            {/* Global Settings */}
            <Heading el="h2" className="fw-medium fs-3 mt-4 mb-3">
                Global settings
            </Heading>
            <Text className="mb-3">
                Bootstrap sets basic global display, typography, and link styles. When more control is needed, check out the textual utility classes.
            </Text>
            <List className="mb-4">
                <ListItem>Use a native font stack that selects the best <Text el="code">font-family</Text> for each OS and device.</ListItem>
                <ListItem>For a more inclusive and accessible type scale, we use the browser's default root <Text el="code">font-size</Text> (typically 16px) so visitors can customize their browser defaults as needed.</ListItem>
                <ListItem>Use the <Text el="code">$font-family-base</Text>, <Text el="code">$font-size-base</Text>, and <Text el="code">$line-height-base</Text> attributes as our typographic base applied to the <Text el="code">&lt;body&gt;</Text>.</ListItem>
                <ListItem>Set the global link color via <Text el="code">$link-color</Text>.</ListItem>
                <ListItem>Use <Text el="code">$body-bg</Text> to set a <Text el="code">background-color</Text> on the <Text el="code">&lt;body&gt;</Text> (<Text el="code">#fff</Text> by default).</ListItem>
            </List>

            {/* Headings */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Headings
            </Heading>
            <Text className="mb-4">
                All HTML headings, <Text el="code">&lt;h1&gt;</Text> through <Text el="code">&lt;h6&gt;</Text>, are available.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <>
                        <Heading el="h1">h1. Bootstrap heading</Heading>
                        <Heading el="h2">h2. Bootstrap heading</Heading>
                        <Heading el="h3">h3. Bootstrap heading</Heading>
                        <Heading el="h4">h4. Bootstrap heading</Heading>
                        <Heading el="h5">h5. Bootstrap heading</Heading>
                        <Heading el="h6">h6. Bootstrap heading</Heading>
                    </>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {HeadingsCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Text className="mb-4">
                <Text el="code">.h1</Text> through <Text el="code">.h6</Text> classes are also available, for when you want to match the font styling of a heading but cannot use the associated HTML element.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <>
                        <Text className="h1">h1. Bootstrap heading</Text>
                        <Text className="h2">h2. Bootstrap heading</Text>
                        <Text className="h3">h3. Bootstrap heading</Text>
                        <Text className="h4">h4. Bootstrap heading</Text>
                        <Text className="h5">h5. Bootstrap heading</Text>
                        <Text className="h6">h6. Bootstrap heading</Text>
                    </>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {HeadingClassesCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Heading colors */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Heading colors
            </Heading>
            <Text className="mb-4">
                Use the <Text el="code">color</Text> prop with <Text el="code">HeadingColor</Text> to apply Bootstrap <Text el="code">text-*</Text> utilities (primary, muted, body emphasis, and the rest of the theme palette).
                When <Text el="code">color</Text> is set, matching <Text el="code">text-*</Text> classes in <Text el="code">className</Text> are removed so the prop stays authoritative.
                For display sizes and <Text el="code">fs-1</Text>–<Text el="code">fs-6</Text>, see <Text el="strong">Display headings</Text> and <Text el="strong">Font size utilities</Text> below.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <>
                        <Heading el="h3" color={HeadingColor.Primary} className="fw-semibold mb-2">
                            Primary
                        </Heading>
                        <Heading el="h3" color={HeadingColor.Success} className="fw-semibold mb-2">
                            Success
                        </Heading>
                        <Heading el="h3" color={HeadingColor.Danger} className="fw-semibold mb-2">
                            Danger
                        </Heading>
                        <Heading el="h3" color={HeadingColor.Muted} className="fw-semibold mb-2">
                            Muted
                        </Heading>
                        <Heading el="h3" color={HeadingColor.BodyEmphasis} className="mb-0">
                            Body emphasis
                        </Heading>
                    </>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {HeadingColorCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Customizing Headings */}
            <Heading el="h3" className="fw-medium fs-5 mt-4 mb-2">
                Customizing headings
            </Heading>
            <Text className="mb-4">
                Use the included utility classes to recreate the small secondary heading text from Bootstrap 3.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <Heading el="h3">
                        Fancy display heading{' '}
                        <Text el="small" className="text-body-secondary">With faded secondary text</Text>
                    </Heading>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {CustomHeadingCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Display Headings */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Display headings
            </Heading>
            <Text className="mb-4">
                Traditional heading elements are designed to work best in the meat of your page content. When you need a heading to stand out, consider using a <Text el="strong">display heading</Text>—a larger, slightly more opinionated heading style.
                With RebootUI, prefer <Text el="code">{`size={HeadingSize.Display1}`}</Text> (and so on) so display classes stay consistent with the <Text el="code">Heading</Text> component API.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <>
                        <Heading el="h1" size={HeadingSize.Display1}>Display 1</Heading>
                        <Heading el="h1" size={HeadingSize.Display2}>Display 2</Heading>
                        <Heading el="h1" size={HeadingSize.Display3}>Display 3</Heading>
                        <Heading el="h1" size={HeadingSize.Display4}>Display 4</Heading>
                        <Heading el="h1" size={HeadingSize.Display5}>Display 5</Heading>
                        <Heading el="h1" size={HeadingSize.Display6}>Display 6</Heading>
                    </>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {DisplayHeadingsCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h3" className="fw-medium fs-5 mt-4 mb-2">
                Font size utilities (<Text el="code">fs-1</Text>–<Text el="code">fs-6</Text>)
            </Heading>
            <Text className="mb-4">
                Use <Text el="code">HeadingSize.Fs1</Text> through <Text el="code">HeadingSize.Fs6</Text> for RFS-based font-size utilities on headings.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <>
                        <Heading el="h2" size={HeadingSize.Fs1} className="mb-2">Heading with fs-1</Heading>
                        <Heading el="h2" size={HeadingSize.Fs2} className="mb-2">Heading with fs-2</Heading>
                        <Heading el="h2" size={HeadingSize.Fs3} className="mb-2">Heading with fs-3</Heading>
                        <Heading el="h2" size={HeadingSize.Fs4} className="mb-2">Heading with fs-4</Heading>
                        <Heading el="h2" size={HeadingSize.Fs5} className="mb-2">Heading with fs-5</Heading>
                        <Heading el="h2" size={HeadingSize.Fs6} className="mb-0">Heading with fs-6</Heading>
                    </>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {HeadingFontSizeCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Lead */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Lead
            </Heading>
            <Text className="mb-4">
                Make a paragraph stand out by adding <Text el="code">.lead</Text>.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <Text el="p" className="lead">
                        This is a lead paragraph. It stands out from regular paragraphs.
                    </Text>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {LeadCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Inline Text Elements */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Inline text elements
            </Heading>
            <Text className="mb-4">
                Styling for common inline HTML5 elements. The Text component now supports all inline text elements.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <>
                        <Text el="p">You can use the mark tag to <Text el="mark">highlight</Text> text.</Text>
                        <Text el="p"><Text el="del">This line of text is meant to be treated as deleted text.</Text></Text>
                        <Text el="p"><Text el="s">This line of text is meant to be treated as no longer accurate.</Text></Text>
                        <Text el="p"><Text el="ins">This line of text is meant to be treated as an addition to the document.</Text></Text>
                        <Text el="p"><Text el="u">This line of text will render as underlined.</Text></Text>
                        <Text el="p"><Text el="small">This line of text is meant to be treated as fine print.</Text></Text>
                        <Text el="p"><Text el="strong">This line rendered as bold text.</Text></Text>
                        <Text el="p"><Text el="em">This line rendered as italicized text.</Text></Text>
                    </>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {InlineElementsCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Text className="mb-4">
                Additional Text component examples with supported inline elements:
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <>
                        <Text el="p">Use <Text el="code">const myVar = 'value';</Text> for inline code.</Text>
                        <Text el="p">Press <Text el="kbd">Ctrl</Text> + <Text el="kbd">C</Text> to copy.</Text>
                        <Text el="p">This is a <Text el="cite">citation element</Text> within text.</Text>
                        <Text el="p">Here is a <Text el="q">quoted text</Text> using the q element.</Text>
                        <Text el="p">The <Text el="var">variable</Text> represents a mathematical variable.</Text>
                        <Text el="p">H<Text el="sub">2</Text>O is water, and E=mc<Text el="sup">2</Text> is Einstein's equation.</Text>
                    </>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {AdditionalInlineElementsCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Text className="mb-4">
                Beware that those tags should be used for semantic purpose:
            </Text>
            <List className="mb-4">
                <ListItem><Text el="code">&lt;mark&gt;</Text> represents text which is marked or highlighted for reference or notation purposes.</ListItem>
                <ListItem><Text el="code">&lt;small&gt;</Text> represents side-comments and small print, like copyright and legal text.</ListItem>
                <ListItem><Text el="code">&lt;s&gt;</Text> represents element that are no longer relevant or no longer accurate.</ListItem>
                <ListItem><Text el="code">&lt;u&gt;</Text> represents a span of inline text which should be rendered in a way that indicates that it has a non-textual annotation.</ListItem>
            </List>

            <Text className="mb-4">
                If you want to style your text, you should use the following classes instead:
            </Text>
            <List className="mb-4">
                <ListItem><Text el="code">.mark</Text> will apply the same styles as <Text el="code">&lt;mark&gt;</Text>.</ListItem>
                <ListItem><Text el="code">.small</Text> will apply the same styles as <Text el="code">&lt;small&gt;</Text>.</ListItem>
                <ListItem><Text el="code">.text-decoration-underline</Text> will apply the same styles as <Text el="code">&lt;u&gt;</Text>.</ListItem>
                <ListItem><Text el="code">.text-decoration-line-through</Text> will apply the same styles as <Text el="code">&lt;s&gt;</Text>.</ListItem>
            </List>

            {/* Abbreviations */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Abbreviations
            </Heading>
            <Text className="mb-4">
                Stylized implementation of HTML's <Text el="code">&lt;abbr&gt;</Text> element for abbreviations and acronyms to show the expanded version on hover. Abbreviations have a default underline and gain a help cursor to provide additional context on hover and to users of assistive technologies.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <>
                        <Text el="p"><Text el="abbr" title="attribute">attr</Text></Text>
                        <Text el="p"><Text el="abbr" title="HyperText Markup Language" className="initialism">HTML</Text></Text>
                    </>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {AbbreviationsCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Blockquotes */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Blockquotes
            </Heading>
            <Text className="mb-4">
                For quoting blocks of content from another source within your document. Wrap <Text el="code">&lt;blockquote className="blockquote"&gt;</Text> around any HTML as the quote.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <Text el="blockquote" className="blockquote">
                        <Text el="p">A well-known quote, contained in a blockquote element.</Text>
                    </Text>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {BlockquoteCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Naming a Source */}
            <Heading el="h3" className="fw-medium fs-5 mt-4 mb-2">
                Naming a source
            </Heading>
            <Text className="mb-4">
                The HTML spec requires that blockquote attribution be placed outside the <Text el="code">&lt;blockquote&gt;</Text>. When providing attribution, wrap your <Text el="code">&lt;blockquote&gt;</Text> in a <Text el="code">&lt;figure&gt;</Text> and use a <Text el="code">&lt;figcaption&gt;</Text> or a block level element (e.g., <Text el="code">&lt;p&gt;</Text>) with the <Text el="code">.blockquote-footer</Text> class. Be sure to wrap the name of the source work in <Text el="code">&lt;cite&gt;</Text> as well.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <Wrapper el="figure">
                        <Text el="blockquote" className="blockquote">
                            <Text el="p">A well-known quote, contained in a blockquote element.</Text>
                        </Text>
                        <Wrapper el="figcaption" className="blockquote-footer">
                            Someone famous in <Text el="cite" title="Source Title">Source Title</Text>
                        </Wrapper>
                    </Wrapper>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {BlockquoteWithSourceCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Alignment */}
            <Heading el="h3" className="fw-medium fs-5 mt-4 mb-2">
                Alignment
            </Heading>
            <Text className="mb-4">
                Use text utilities as needed to change the alignment of your blockquote.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <>
                        <Wrapper el="figure" className="text-center">
                            <Text el="blockquote" className="blockquote">
                                <Text el="p">A well-known quote, contained in a blockquote element.</Text>
                            </Text>
                            <Wrapper el="figcaption" className="blockquote-footer">
                                Someone famous in <Text el="cite" title="Source Title">Source Title</Text>
                            </Wrapper>
                        </Wrapper>
                        <Wrapper el="figure" className="text-end">
                            <Text el="blockquote" className="blockquote">
                                <Text el="p">A well-known quote, contained in a blockquote element.</Text>
                            </Text>
                            <Wrapper el="figcaption" className="blockquote-footer">
                                Someone famous in <Text el="cite" title="Source Title">Source Title</Text>
                            </Wrapper>
                        </Wrapper>
                    </>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {BlockquoteAlignmentCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Lists */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Lists
            </Heading>

            {/* Unstyled */}
            <Heading el="h3" className="fw-medium fs-5 mt-4 mb-2">
                Unstyled
            </Heading>
            <Text className="mb-4">
                Remove the default <Text el="code">list-style</Text> and left margin on list items (immediate children only). <Text el="strong">This only applies to immediate children list items</Text>, meaning you will need to add the class for any nested lists as well.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <List className="list-unstyled">
                        <ListItem>This is a list.</ListItem>
                        <ListItem>It appears completely unstyled.</ListItem>
                        <ListItem>Structurally, it's still a list.</ListItem>
                        <ListItem>However, this style only applies to immediate child elements.</ListItem>
                        <ListItem>
                            Nested lists:
                            <List>
                                <ListItem>are unaffected by this style</ListItem>
                                <ListItem>will still show a bullet</ListItem>
                                <ListItem>and have appropriate left margin</ListItem>
                            </List>
                        </ListItem>
                        <ListItem>This may still come in handy in some situations.</ListItem>
                    </List>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {UnstyledListCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Inline Lists */}
            <Heading el="h3" className="fw-medium fs-5 mt-4 mb-2">
                Inline
            </Heading>
            <Text className="mb-4">
                Remove a list's bullets and apply some light margin with a combination of two classes, <Text el="code">.list-inline</Text> and <Text el="code">.list-inline-item</Text>.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <List className="list-inline">
                        <ListItem className="list-inline-item">This is a list item.</ListItem>
                        <ListItem className="list-inline-item">And another one.</ListItem>
                        <ListItem className="list-inline-item">But they're displayed inline.</ListItem>
                    </List>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {InlineListCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Description List Alignment */}
            <Heading el="h3" className="fw-medium fs-5 mt-4 mb-2">
                Description list alignment
            </Heading>
            <Text className="mb-4">
                Align terms and descriptions horizontally by using our grid system's predefined classes (or semantic mixins). For longer terms, you can optionally add a <Text el="code">.text-truncate</Text> class to truncate the text with an ellipsis.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <List el="dl" className="row">
                        <ListItem el="dt" className="col-sm-3">Description lists</ListItem>
                        <ListItem el="dd" className="col-sm-9">A description list is perfect for defining terms.</ListItem>

                        <ListItem el="dt" className="col-sm-3">Term</ListItem>
                        <ListItem el="dd" className="col-sm-9">
                            <Text el="p">Definition for the term.</Text>
                            <Text el="p">And some more placeholder definition text.</Text>
                        </ListItem>

                        <ListItem el="dt" className="col-sm-3">Another term</ListItem>
                        <ListItem el="dd" className="col-sm-9">This definition is short, so no extra paragraphs or anything.</ListItem>

                        <ListItem el="dt" className="col-sm-3 text-truncate">Truncated term is truncated</ListItem>
                        <ListItem el="dd" className="col-sm-9">This can be useful when space is tight. Adds an ellipsis at the end.</ListItem>

                        <ListItem el="dt" className="col-sm-3">Nesting</ListItem>
                        <ListItem el="dd" className="col-sm-9">
                            <List el="dl" className="row">
                                <ListItem el="dt" className="col-sm-4">Nested definition list</ListItem>
                                <ListItem el="dd" className="col-sm-8">I heard you like definition lists. Let me put a definition list inside your definition list.</ListItem>
                            </List>
                        </ListItem>
                    </List>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {DescriptionListCode}
                </CodeHighlighter>
            </CodeSnippet>
        </Container>
    );
}

export default TypographyPage;

// Code snippets
const HeadingsCode = `<Heading el="h1">h1. Bootstrap heading</Heading>
<Heading el="h2">h2. Bootstrap heading</Heading>
<Heading el="h3">h3. Bootstrap heading</Heading>
<Heading el="h4">h4. Bootstrap heading</Heading>
<Heading el="h5">h5. Bootstrap heading</Heading>
<Heading el="h6">h6. Bootstrap heading</Heading>`;

const HeadingClassesCode = `<Text className="h1">h1. Bootstrap heading</Text>
<Text className="h2">h2. Bootstrap heading</Text>
<Text className="h3">h3. Bootstrap heading</Text>
<Text className="h4">h4. Bootstrap heading</Text>
<Text className="h5">h5. Bootstrap heading</Text>
<Text className="h6">h6. Bootstrap heading</Text>`;

const CustomHeadingCode = `<Heading el="h3">
    Fancy display heading{' '}
    <Text el="small" className="text-body-secondary">With faded secondary text</Text>
</Heading>`;

const HeadingColorCode = `import { Heading, HeadingColor } from '@reactufy/rebootui';

<Heading el="h3" color={HeadingColor.Primary} className="fw-semibold mb-2">
  Primary
</Heading>
<Heading el="h3" color={HeadingColor.Success} className="fw-semibold mb-2">
  Success
</Heading>
<Heading el="h3" color={HeadingColor.Danger} className="fw-semibold mb-2">
  Danger
</Heading>
<Heading el="h3" color={HeadingColor.Muted} className="fw-semibold mb-2">
  Muted
</Heading>
<Heading el="h3" color={HeadingColor.BodyEmphasis} className="mb-0">
  Body emphasis
</Heading>`;

const DisplayHeadingsCode = `import { Heading, HeadingSize } from '@reactufy/rebootui';

<Heading el="h1" size={HeadingSize.Display1}>Display 1</Heading>
<Heading el="h1" size={HeadingSize.Display2}>Display 2</Heading>
<Heading el="h1" size={HeadingSize.Display3}>Display 3</Heading>
<Heading el="h1" size={HeadingSize.Display4}>Display 4</Heading>
<Heading el="h1" size={HeadingSize.Display5}>Display 5</Heading>
<Heading el="h1" size={HeadingSize.Display6}>Display 6</Heading>`;

const HeadingFontSizeCode = `import { Heading, HeadingSize } from '@reactufy/rebootui';

<Heading el="h2" size={HeadingSize.Fs1} className="mb-2">Heading with fs-1</Heading>
<Heading el="h2" size={HeadingSize.Fs2} className="mb-2">Heading with fs-2</Heading>
<Heading el="h2" size={HeadingSize.Fs3} className="mb-2">Heading with fs-3</Heading>
<Heading el="h2" size={HeadingSize.Fs4} className="mb-2">Heading with fs-4</Heading>
<Heading el="h2" size={HeadingSize.Fs5} className="mb-2">Heading with fs-5</Heading>
<Heading el="h2" size={HeadingSize.Fs6} className="mb-0">Heading with fs-6</Heading>`;

const LeadCode = `<Text el="p" className="lead">
    This is a lead paragraph. It stands out from regular paragraphs.
</Text>`;

const InlineElementsCode = `<Text el="p">
    You can use the mark tag to <Text el="mark">highlight</Text> text.
</Text>
<Text el="p">
    <Text el="del">This line of text is meant to be treated as deleted text.</Text>
</Text>
<Text el="p">
    <Text el="s">This line of text is meant to be treated as no longer accurate.</Text>
</Text>
<Text el="p">
    <Text el="ins">This line of text is meant to be treated as an addition to the document.</Text>
</Text>
<Text el="p">
    <Text el="u">This line of text will render as underlined.</Text>
</Text>
<Text el="p">
    <Text el="small">This line of text is meant to be treated as fine print.</Text>
</Text>
<Text el="p">
    <Text el="strong">This line rendered as bold text.</Text>
</Text>
<Text el="p">
    <Text el="em">This line rendered as italicized text.</Text>
</Text>`;

const AdditionalInlineElementsCode = `<Text el="p">
    Use <Text el="code">const myVar = 'value';</Text> for inline code.
</Text>
<Text el="p">
    Press <Text el="kbd">Ctrl</Text> + <Text el="kbd">C</Text> to copy.
</Text>
<Text el="p">
    This is a <Text el="cite">citation element</Text> within text.
</Text>
<Text el="p">
    Here is a <Text el="q">quoted text</Text> using the q element.
</Text>
<Text el="p">
    The <Text el="var">variable</Text> represents a mathematical variable.
</Text>
<Text el="p">
    H<Text el="sub">2</Text>O is water, and E=mc<Text el="sup">2</Text> is Einstein's equation.
</Text>`;

const AbbreviationsCode = `<Text el="p">
    <Text el="abbr" title="attribute">attr</Text>
</Text>
<Text el="p">
    <Text el="abbr" title="HyperText Markup Language" className="initialism">HTML</Text>
</Text>`;

const BlockquoteCode = `<Text el="blockquote" className="blockquote">
    <Text el="p">A well-known quote, contained in a blockquote element.</Text>
</Text>`;

const BlockquoteWithSourceCode = `<Wrapper el="figure">
    <Text el="blockquote" className="blockquote">
        <Text el="p">A well-known quote, contained in a blockquote element.</Text>
    </Text>
    <Wrapper el="figcaption" className="blockquote-footer">
        Someone famous in <Text el="cite" title="Source Title">Source Title</Text>
    </Wrapper>
</Wrapper>`;

const BlockquoteAlignmentCode = `<Wrapper el="figure" className="text-center">
    <Text el="blockquote" className="blockquote">
        <Text el="p">A well-known quote, contained in a blockquote element.</Text>
    </Text>
    <Wrapper el="figcaption" className="blockquote-footer">
        Someone famous in <Text el="cite" title="Source Title">Source Title</Text>
    </Wrapper>
</Wrapper>

<Wrapper el="figure" className="text-end">
    <Text el="blockquote" className="blockquote">
        <Text el="p">A well-known quote, contained in a blockquote element.</Text>
    </Text>
    <Wrapper el="figcaption" className="blockquote-footer">
        Someone famous in <Text el="cite" title="Source Title">Source Title</Text>
    </Wrapper>
</Wrapper>`;

const UnstyledListCode = `<List className="list-unstyled">
    <ListItem>This is a list.</ListItem>
    <ListItem>It appears completely unstyled.</ListItem>
    <ListItem>Structurally, it's still a list.</ListItem>
    <ListItem>However, this style only applies to immediate child elements.</ListItem>
    <ListItem>
        Nested lists:
        <List>
            <ListItem>are unaffected by this style</ListItem>
            <ListItem>will still show a bullet</ListItem>
            <ListItem>and have appropriate left margin</ListItem>
        </List>
    </ListItem>
    <ListItem>This may still come in handy in some situations.</ListItem>
</List>`;

const InlineListCode = `<List className="list-inline">
    <ListItem className="list-inline-item">This is a list item.</ListItem>
    <ListItem className="list-inline-item">And another one.</ListItem>
    <ListItem className="list-inline-item">But they're displayed inline.</ListItem>
</List>`;

const DescriptionListCode = `<List el="dl" className="row">
    <ListItem el="dt" className="col-sm-3">Description lists</ListItem>
    <ListItem el="dd" className="col-sm-9">A description list is perfect for defining terms.</ListItem>

    <ListItem el="dt" className="col-sm-3">Term</ListItem>
    <ListItem el="dd" className="col-sm-9">
        <Text el="p">Definition for the term.</Text>
        <Text el="p">And some more placeholder definition text.</Text>
    </ListItem>

    <ListItem el="dt" className="col-sm-3">Another term</ListItem>
    <ListItem el="dd" className="col-sm-9">This definition is short, so no extra paragraphs or anything.</ListItem>

    <ListItem el="dt" className="col-sm-3 text-truncate">Truncated term is truncated</ListItem>
    <ListItem el="dd" className="col-sm-9">This can be useful when space is tight. Adds an ellipsis at the end.</ListItem>

    <ListItem el="dt" className="col-sm-3">Nesting</ListItem>
    <ListItem el="dd" className="col-sm-9">
        <List el="dl" className="row">
            <ListItem el="dt" className="col-sm-4">Nested definition list</ListItem>
            <ListItem el="dd" className="col-sm-8">I heard you like definition lists. Let me put a definition list inside your definition list.</ListItem>
        </List>
    </ListItem>
</List>`;
